import {Table} from '@sollapay/ui/components';
import {useCallback, useMemo} from 'react';

import {TrustAccountStatusGroupHeader} from './TrustAccountStatusGroupHeader';

import type {TrustAccountListColumn} from './list-columns';
import type {TrustAccountGroupFold, TrustStatusGroup} from '../utils';
import type {TrustStatus} from '@sollapay/enums';
import type {ListOnSortingChange, ListSortingState, TableProps} from '@sollapay/ui/components';

interface GroupedRow {
  trustStatus: TrustStatus;
}

export interface TrustAccountGroupedTableProps<Row extends GroupedRow> extends Pick<
  TableProps<Row, unknown>,
  'variant' | 'minWidth' | 'rowLink' | 'emptyStateText'
> {
  columns: TrustAccountListColumn<Row>[];
  groups: TrustStatusGroup<Row>[];
  groupFold: TrustAccountGroupFold;
  sorting?: ListSortingState;
  onSortingChange?: ListOnSortingChange;
}

/** A table with one foldable divider per status group (from `groupByTrustStatus`), in group order. */
export function TrustAccountGroupedTable<Row extends GroupedRow>({
  groups,
  groupFold,
  ...tableProps
}: TrustAccountGroupedTableProps<Row>) {
  // Laid out in group order up front; `manualSorting` keeps that order, so groups stay contiguous.
  const rows = useMemo(() => groups.flatMap(group => group.items), [groups]);
  const renderGroupHeader = useCallback(
    (row: Row, groupCount: number) => (
      <TrustAccountStatusGroupHeader
        status={row.trustStatus}
        count={groupCount}
        isCollapsed={groupFold.isCollapsed(row.trustStatus)}
        onToggle={() => groupFold.toggle(row.trustStatus)}
      />
    ),
    [groupFold]
  );
  const isRowCollapsed = useCallback(
    (row: Row) => groupFold.isCollapsed(row.trustStatus),
    [groupFold]
  );

  return (
    <Table
      {...tableProps}
      data={rows}
      manualSorting
      getGroupKey={getStatusGroupKey}
      renderGroupHeader={renderGroupHeader}
      isRowCollapsed={isRowCollapsed}
    />
  );
}

// Module-level so the table's group-boundary memo isn't invalidated every render.
const getStatusGroupKey = (row: GroupedRow) => row.trustStatus;
